// Workspace Admin — 3 tabs on a single page:
//   1. Teams        — create/edit teams + membership
//   2. Catalogs     — edit statuses + task types (per-Space custom fields
//                     land here in Phase 2 — MVP treats it as read-only)
//   3. Export       — CSV download (owner + system_admin only, backend
//                     enforces; UI still surfaces the button)
import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import {
  Users, ListChecks, Download, Upload, Plus, Trash2, Save, Loader2, RefreshCw, FileUp, ScrollText, EyeOff,
} from "lucide-react";
import TestDataCleanup from "./panels/TestDataCleanup";
import AdminAuditLog from "./AdminAuditLog";
import ImportWizard from "./panels/ImportWizard";
import TeamCard from "./panels/TeamCard";
import { download } from "./panels/ListToolbar";
import "./workspace.css";
import { workspaceApi, isMgmt, teamColor } from "./_shared";

const TABS = [
  { key: "teams",    label: "Teams",             icon: Users },
  { key: "catalogs", label: "Statuses & Types",  icon: ListChecks },
  { key: "routines", label: "Routines",          icon: RefreshCw },
  { key: "import",   label: "Import",            icon: Upload },
  { key: "export",   label: "Export",            icon: Download },
  { key: "audit",    label: "Audit log",         icon: ScrollText },
  { key: "testdata", label: "Test data",         icon: EyeOff },
];

export default function WorkspaceAdmin() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "teams");
  const { user } = useAuth();
  if (user && !isMgmt(user)) return <Navigate to="/workspace" replace />;
  return (
    <div className="space-y-4" data-testid="workspace-admin">
      <div className="flex items-center gap-1 border-b border-white/[0.06] overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest border-b-2 -mb-px ${
                tab === t.key ? "border-amber-400 text-amber-200" : "border-transparent text-slate-400 hover:text-white"
              }`}
              data-testid={`ws-admin-tab-${t.key}`}>
              <Icon className="w-3 h-3" /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "teams"    && <TeamsAdmin    />}
      {tab === "catalogs" && <CatalogsAdmin />}
      {tab === "routines" && <RoutinesAdmin />}
      {tab === "import"   && <ImportAdmin   />}
      {tab === "export"   && <ExportAdmin   />}
      {tab === "audit"    && <AdminAuditLog />}
      {tab === "testdata" && <TestDataCleanup />}
    </div>
  );
}

// ─── Teams tab ─────────────────────────────────────────────────────────
function TeamsAdmin() {
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState("slate");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [tr, ur] = await Promise.all([workspaceApi.listTeams("all"), workspaceApi.listUsers()]);
      setTeams(tr.items || []); setUsers(ur || []);
    } catch { toast.error("Failed to load teams"); }
  };
  useEffect(() => { load(); }, []);

  const doCreate = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      await workspaceApi.createTeam({ name: name.trim(), description, color });
      toast.success("Team created");
      setName(""); setDescription(""); setColor("slate"); setCreating(false);
      load();
    } catch (e) { toast.error("Create failed", { description: e?.response?.data?.detail || "" }); }
    finally { setSaving(false); }
  };

  const refreshSelected = async () => {
    const r = await workspaceApi.listTeams("all");
    setTeams(r.items || []);
    setSelected((cur) => (r.items || []).find((t) => t.id === cur?.id) || null);
  };

  const toggleMember = async (uid) => {
    if (!selected) return;
    const isMember = selected.member_ids?.includes(uid);
    try {
      await workspaceApi.editMembership(selected.id,
        isMember ? [] : [uid],
        isMember ? [uid] : []);
      await refreshSelected();
    } catch { toast.error("Membership update failed"); }
  };

  return (
    <div className="space-y-4" data-testid="ws-admin-teams">
      {/* Horizontal team chip strip — matches Home + Teams pages. */}
      <div className="ws-team-strip" data-testid="ws-admin-teams-strip">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono flex items-center gap-1.5 mr-2 self-center">
          <Users className="w-3 h-3" /> Teams · {teams.length}
        </div>
        {teams.map((t) => (
          <button key={t.id}
            onClick={() => { setCreating(false); setSelected(t); }}
            className="ws-team-chip"
            data-active={selected?.id === t.id ? "true" : undefined}
            data-testid={`ws-admin-team-${t.id}`}>
            <span className="dot" style={{ background: teamColor(t) }} />
            {t.name}{t.visibility === "private" && <span title="Private team">🔒</span>}
            <span className="count">{t.people_count ?? (t.member_ids || []).length} people · {t.open_count ?? 0} open</span>
          </button>
        ))}
        <button onClick={() => { setCreating(true); setSelected(null); }}
          className="ws-team-chip"
          style={{ borderStyle: "dashed" }}
          data-testid="ws-admin-team-add">
          <Plus className="w-3 h-3" /> New team
        </button>
      </div>

      {/* Detail / Create — full-width */}
      <div className="ws-card space-y-3">
        {creating ? (
          <>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono">New team</div>
            <div className="grid gap-3 max-w-xl">
              <input className="ws-input w-full" placeholder="Team name" value={name} onChange={(e) => setName(e.target.value)} data-testid="ws-admin-new-team-name" />
              <textarea rows={2} className="ws-input w-full" placeholder="What does this team look after? (optional)" value={description} onChange={(e) => setDescription(e.target.value)} />
              <div className="flex items-center gap-2" data-testid="ws-admin-new-team-color">
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mr-1">Colour</span>
                {["slate", "sky", "teal", "amber", "orange", "rose", "purple", "fuchsia", "emerald"].map((c) => (
                  <button key={c} type="button" onClick={() => setColor(c)} title={c} data-testid={`ws-admin-team-color-${c}`}
                    className={`w-5 h-5 rounded-full bg-${c}-400 ${color === c ? "ring-2 ring-offset-2 ring-offset-slate-950 ring-white" : "opacity-60 hover:opacity-100"}`} />
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={doCreate} disabled={saving}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-amber-500 hover:bg-amber-400 text-slate-900 font-mono uppercase tracking-widest text-[11px]"
                data-testid="ws-admin-new-team-save">
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Create
              </button>
              <button onClick={() => setCreating(false)} className="text-slate-400 hover:text-white text-[11px] font-mono">Cancel</button>
            </div>
          </>
        ) : selected ? (
          <>
            <TeamCard team={selected} users={users} canEdit canEditAll onSaved={refreshSelected} />
            <div className="text-[11.5px] text-slate-500">{(selected.visibility || "public") === "private" ? "Private: only members, admins and Management can see, count or get notified about this team's tasks." : "Public: anyone can see this team's tasks (unless a task is marked private)."} Use <b>Edit team</b> to change name, colour, description, rota, visibility and team lead(s).</div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">Members ({users.filter((u) => selected.member_ids?.includes(u.id)).length}/{users.length})</div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-1">
                {users.map((u) => {
                  const isMember = selected.member_ids?.includes(u.id);
                  return (
                    <label key={u.id} className="flex items-center gap-2 text-[12px] py-0.5 hover:bg-white/[0.02] px-1 rounded cursor-pointer" data-testid={`ws-admin-member-${u.id}`}>
                      <input type="checkbox" checked={!!isMember} onChange={() => toggleMember(u.id)} />
                      <span className={`truncate ${isMember ? "text-slate-100" : "text-slate-400"}`}>{u.name || u.email}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          <div className="text-center text-slate-500 py-10 text-[13px]">
            Pick a team above, or click <b>+ New team</b>.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Catalogs tab ──────────────────────────────────────────────────────
function CatalogsAdmin() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { workspaceApi.getConfig().then(setConfig); }, []);

  const patch = async (patchObj) => {
    setSaving(true);
    try {
      const r = await workspaceApi.patchConfig(patchObj);
      setConfig(r);
      toast.success("Catalog saved");
    } catch (e) { toast.error("Save failed", { description: e?.response?.data?.detail || "" }); }
    finally { setSaving(false); }
  };

  if (!config) return <div className="text-slate-500 text-center py-10">Loading…</div>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="ws-admin-catalogs">
      <CatalogList kind="statuses" title="Statuses" items={config.statuses} onSave={(next) => patch({ statuses: next })} saving={saving} withColor />
      <CatalogList kind="types"    title="Task types" items={config.types}   onSave={(next) => patch({ types: next })}    saving={saving} />
    </div>
  );
}
function CatalogList({ kind, title, items, onSave, saving, withColor }) {
  const [local, setLocal] = useState(items);
  useEffect(() => setLocal(items), [items]);
  const upd = (i, k, v) => setLocal(local.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const add = () => setLocal([...local, { key: "", label: "", ...(withColor && { color: "slate" }) }]);
  const del = (i) => setLocal(local.filter((_, idx) => idx !== i));
  return (
    <div className="ws-card space-y-2" data-testid={`ws-admin-catalog-${kind}`}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono">{title}</div>
        <div className="flex items-center gap-1">
          <button onClick={add} className="text-slate-400 hover:text-white p-1" title="Add"><Plus className="w-3.5 h-3.5" /></button>
          <button onClick={() => onSave(local)} disabled={saving}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 uppercase tracking-widest text-[9px] font-mono"
            data-testid={`ws-admin-catalog-save-${kind}`}>
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
          </button>
        </div>
      </div>
      {local.map((row, i) => (
        <div key={i} className="grid grid-cols-[80px_1fr_80px_28px] gap-2 items-center">
          <input className="ws-input" value={row.key || ""}
            onChange={(e) => upd(i, "key", e.target.value.replace(/[^a-z0-9_]/g, "_").toLowerCase())} placeholder="key" />
          <input className="ws-input" value={row.label || ""} onChange={(e) => upd(i, "label", e.target.value)} placeholder="Label" />
          {withColor ? (
            <select className="ws-input" value={row.color || "slate"} onChange={(e) => upd(i, "color", e.target.value)}>
              {["slate", "sky", "teal", "amber", "orange", "rose", "purple", "fuchsia", "emerald"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          ) : <span className="text-slate-600 text-center">—</span>}
          <button onClick={() => del(i)} className="text-rose-400 hover:text-rose-300 p-1"><Trash2 className="w-3 h-3" /></button>
        </div>
      ))}
    </div>
  );
}

// ─── Export tab ────────────────────────────────────────────────────────
function ExportAdmin() {
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState("");
  useEffect(() => { workspaceApi.listTeams("all").then((r) => setTeams(r.items || [])); }, []);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const qs = new URLSearchParams(Object.entries({ from, to }).filter(([, v]) => v)).toString();
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ws-admin-export">
      <div className="ws-card space-y-3">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono">Workspace tasks file</div>
        <p className="text-[12.5px] text-slate-400">Every company task with team, owner, subtasks, tags, watchers, repeats, linked CRM records and created / updated / completed dates. Same columns as the import file.</p>
        <div className="flex items-center gap-2">
          <select className="ws-input max-w-[240px]" value={teamId} onChange={(e) => setTeamId(e.target.value)} data-testid="ws-admin-export-team">
            <option value="">All teams</option>
            {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <button onClick={() => download(`/export.csv?scope=all${teamId ? `&team_id=${teamId}` : ""}`, "workspace-tasks.csv")} data-testid="ws-admin-export-download"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-amber-500 hover:bg-amber-400 text-slate-900 font-mono uppercase tracking-widest text-[11px]"><Download className="w-3.5 h-3.5" /> Download</button>
        </div>
      </div>
      <div className="ws-card space-y-3">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono">CRM activities file</div>
        <p className="text-[12.5px] text-slate-400">Every customer activity: type, subject, notes, status, outcome, direction, customer, quote, owner and dates. Same columns as the import file.</p>
        <div className="flex flex-wrap items-center gap-2">
          <input type="date" className="ws-input max-w-[150px]" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="ws-admin-export-act-from" />
          <span className="text-slate-500 text-[12px]">to</span>
          <input type="date" className="ws-input max-w-[150px]" value={to} onChange={(e) => setTo(e.target.value)} data-testid="ws-admin-export-act-to" />
          <button onClick={() => download(`/api/crm/activities-io/export.csv${qs ? `?${qs}` : ""}`, "crm-activities.csv")} data-testid="ws-admin-export-activities"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-orange-500 hover:bg-orange-400 text-slate-900 font-mono uppercase tracking-widest text-[11px]"><Download className="w-3.5 h-3.5" /> Download</button>
        </div>
      </div>
    </div>
  );
}

function WizardCard({ kind, title, blurb }) {
  const [open, setOpen] = useState(false);
  const dl = kind === "tasks" ? "" : "/api/crm/activities-io";
  return (
    <div className="ws-card space-y-3" data-testid={`ws-import-${kind}`}>
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono">{title}</div>
      <p className="text-[12.5px] text-slate-400">{blurb}</p>
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setOpen(true)} data-testid={`ws-import-${kind}-open`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-fuchsia-500/20 border border-fuchsia-400/50 text-fuchsia-100 text-[12px]"><Upload className="w-3.5 h-3.5" /> Upload & preview</button>
        <button onClick={() => download(`${dl}/import/template.csv`, `${kind}-template.csv`)} className="ws-chip" data-testid={`ws-import-${kind}-template`}><FileUp className="w-3.5 h-3.5 text-emerald-300" />Template</button>
        <button onClick={() => download(`${dl}/import/guide.md`, `${kind}-guide.md`)} className="ws-chip" data-testid={`ws-import-${kind}-guide`}><ScrollText className="w-3.5 h-3.5 text-violet-300" />Guide</button>
      </div>
      {open && <ImportWizard kind={kind} onClose={() => setOpen(false)} onDone={() => setOpen(false)} />}
    </div>
  );
}


// ─── Routines tab ──────────────────────────────────────────────────────
function RoutinesAdmin() {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => workspaceApi.routinesPreview().then(setP).catch(() => setP({ error: true }));
  useEffect(() => { load(); }, []);
  const run = async (fn, msg) => { setBusy(true); try { const r = await fn(); toast.success(msg(r)); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); } };
  if (!p) return <div className="text-slate-500 text-center py-10">Loading…</div>;
  return (
    <div className="ws-card space-y-3 max-w-3xl" data-testid="ws-admin-routines">
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono">Switch repeating tasks to Routines</div>
      <p className="text-[12.5px] text-slate-300">Routines are one standing card per duty. People tick each slot (every 30 min, hourly, daily, weekly or monthly), one tick catches up missed slots, the team sees who ticked, and only a missed slot sends one quiet nudge. No more daily copies, overdue alerts or weekly “I'm on this” check-ins.</p>
      <div className="grid grid-cols-3 gap-2 text-center">
        {[["routines", "repeating series → 1 routine card each"], ["archive_copies", "open duplicate copies archived (not deleted)"], ["checkins", "weekly check-ins → weekly routines"]].map(([k, l]) => (
          <div key={k} className="rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid={`ws-routines-count-${k}`}><div className="text-2xl font-semibold text-white">{p[k] ?? 0}</div><div className="text-[11px] text-slate-400">{l}</div></div>
        ))}
      </div>
      {(p.sample || []).length > 0 && <div className="text-[11.5px] text-slate-400">e.g. {(p.sample || []).slice(0, 6).map((s) => `${s.ref} ${s.title} (${s.rule})`).join(" · ")}</div>}
      <div className="flex flex-wrap items-center gap-2">
        <button disabled={busy || !(p.routines || p.checkins)} onClick={() => run(workspaceApi.routinesConvert, (r) => `Converted ${r.routines} routines · archived ${r.archived_copies} copies · ${r.checkins} check-ins`)}
          className="px-4 py-2 rounded-lg bg-violet-500/25 border border-violet-300/50 text-violet-50 text-[12.5px] disabled:opacity-40" data-testid="ws-routines-convert">Convert now</button>
        {p.converted_before > 0 && <button disabled={busy} onClick={() => run(workspaceApi.routinesUndo, (r) => `Restored ${r.restored} tasks`)} className="ws-chip" data-testid="ws-routines-undo">Undo conversion ({p.converted_before} tasks)</button>}
        <span className="text-[11px] text-slate-500">Safe: every task is snapshotted first and can be restored with Undo.</span>
      </div>
    </div>
  );
}

// ─── Import tab ────────────────────────────────────────────────────────
// Three dedicated import cards.  Each downloads a CSV template so users
// can hand-edit in Excel and re-upload safely.
function ImportAdmin() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ws-admin-import">
      <WizardCard kind="tasks" title="Workspace tasks" blurb="Full task file: details, team(s), owner, subtasks, tags, watchers, repeats, linked CRM records and history dates. Preview + duplicate check before anything saves." />
      <WizardCard kind="activities" title="CRM activities" blurb="Customer work file: calls, emails, chases, follow-ups with outcome, customer, quote, owner and dates. Preview + duplicate check before anything saves." />
      <ImportCard
        testid="ws-import-roster"
        title="Import Roster"
        blurb="Bulk-upsert users + wire them into teams. Matches by email (case-insensitive). Team IDs OR names accepted."
        templateName="workspace-roster-template.csv"
        templateHeader="name,email,role,role_workspace,job_title,teams"
        templateSample={[
          `"Allen Aceret",allen.aceret@t.talmia.com,user,,BDR,"outbound-sales;inbound-cs"`,
          `"Aiste B",aiste.b@t.talmia.com,manager,manager,"Head of Sales",Management`,
        ]}
        endpoint={workspaceApi.importRoster}
        splitCols={{ teams: (v) => (v || "").split(";").map(x => x.trim()).filter(Boolean) }}
      />
      <ImportCard
        testid="ws-import-orgchart"
        title="Import Org Chart"
        blurb="Bulk-set reporting lines + out-of-office flags. user_id + reports_to are user IDs (see Full Squad)."
        templateName="workspace-orgchart-template.csv"
        templateHeader="user_id,reports_to,out_of_office_reason,out_of_office_until"
        templateSample={[
          `allen-aceret,aiste-b,,`,
          `maita-den-cayabyab,niraj-g,Sick,2026-02-20`,
        ]}
        endpoint={workspaceApi.importOrgChart}
      />
    </div>
  );
}

function ImportCard({ testid, title, blurb, templateName, templateHeader, templateSample, endpoint, splitCols = {} }) {
  const [dropping, setDropping] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = { current: null };

  const downloadTemplate = () => {
    // Use a real <a download> so right-click "Save link as…" also works —
    // Blob URL + anchor click is the canonical browser pattern.
    const csv = [templateHeader, ...templateSample].join("\n") + "\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = templateName;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 500);
    toast.success("Template downloaded");
  };

  const parseAndUpload = async (file) => {
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const text = await file.text();
      const rows = parseCSV(text);
      if (rows.length < 2) throw new Error("CSV appears empty");
      const header = rows[0].map(h => h.trim().toLowerCase());
      const payload = rows.slice(1).map((r) => {
        const obj = {};
        header.forEach((h, i) => {
          const raw = r[i] ?? "";
          obj[h] = splitCols[h] ? splitCols[h](raw) : (raw === "" ? null : raw);
        });
        return obj;
      }).filter(o => Object.values(o).some(v => v !== null && v !== "" && (Array.isArray(v) ? v.length : true)));
      if (!payload.length) throw new Error("No valid rows found");
      const res = await endpoint(payload);
      setResult(res);
      toast.success("Import complete", {
        description: [
          res.inserted != null ? `${res.inserted} inserted` : null,
          res.updated  != null ? `${res.updated} updated`   : null,
          res.applied  != null ? `${res.applied} applied`   : null,
          res.errors?.length ? `${res.errors.length} errors` : null,
        ].filter(Boolean).join(" · "),
      });
    } catch (e) {
      toast.error("Import failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  return (
    <div className="ws-card space-y-3 flex flex-col" data-testid={testid}>
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono flex items-center gap-1.5">
          <FileUp className="w-3 h-3" /> {title}
        </div>
        <p className="text-[11.5px] text-slate-400 mt-1 leading-relaxed">{blurb}</p>
      </div>

      <button onClick={downloadTemplate}
        className="text-left text-[11px] font-mono text-sky-300 hover:text-sky-200"
        data-testid={`${testid}-template`}
      >
        ↓ Download {templateName}
      </button>

      <div
        onDragOver={(e) => { e.preventDefault(); setDropping(true); }}
        onDragLeave={() => setDropping(false)}
        onDrop={(e) => {
          e.preventDefault(); setDropping(false);
          parseAndUpload(e.dataTransfer.files?.[0]);
        }}
        className={`rounded border-2 border-dashed py-6 px-3 text-center transition-colors ${dropping ? "border-amber-400 bg-amber-500/10" : "border-white/10 bg-white/[0.02]"}`}
      >
        <div className="text-[11.5px] text-slate-400">
          Drop CSV here, or{" "}
          <label className="text-amber-300 hover:text-amber-200 cursor-pointer underline underline-offset-2">
            browse
            <input type="file" accept=".csv,text/csv" className="hidden"
              onChange={(e) => parseAndUpload(e.target.files?.[0])}
              data-testid={`${testid}-file`}
            />
          </label>
        </div>
        {busy && <div className="text-[10px] font-mono text-amber-300 mt-2">Uploading…</div>}
      </div>

      {result && (
        <div className="text-[11px] font-mono text-slate-300 space-y-0.5" data-testid={`${testid}-result`}>
          {result.inserted != null && <div>✓ Inserted: <b>{result.inserted}</b></div>}
          {result.updated  != null && <div>✓ Updated:  <b>{result.updated}</b></div>}
          {result.applied  != null && <div>✓ Applied:  <b>{result.applied}</b></div>}
          {result.errors?.length > 0 && (
            <details className="text-rose-300">
              <summary>{result.errors.length} error(s)</summary>
              <ul className="ml-3 mt-1 space-y-0.5">
                {result.errors.slice(0, 10).map((e, i) => (
                  <li key={i}>row {e.row}: {e.error}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

// Lightweight CSV parser copied from ListToolbar — kept local so the Admin
// tab doesn't depend on any list-page state.
function parseCSV(text) {
  const rows = [];
  let cur = [], cell = "", quoted = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i], n = text[i + 1];
    if (quoted) {
      if (c === '"' && n === '"') { cell += '"'; i++; }
      else if (c === '"') { quoted = false; }
      else cell += c;
    } else {
      if (c === '"') quoted = true;
      else if (c === ",") { cur.push(cell); cell = ""; }
      else if (c === "\n") { cur.push(cell); rows.push(cur); cur = []; cell = ""; }
      else if (c === "\r") { /* skip */ }
      else cell += c;
    }
  }
  if (cell.length || cur.length) { cur.push(cell); rows.push(cur); }
  return rows.filter(r => r.length > 1 || (r[0] || "").trim().length);
}
